import { useState } from 'react';

import { GEOCODING_URL, RESULT_LIMIT } from './constants';
import type { FetchSuggestions, GeocodeResponse, GeocodeResult } from './types';

const ACCESS_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN as string;

const useGeocode = () => {
    const [isLoading, setIsLoading] = useState(false);

    const fetchSuggestions: FetchSuggestions = async ({ query }) => {
        const trimmedQuery = query.trim();

        if (!trimmedQuery) {
            return [];
        }

        try {
            setIsLoading(true);

            const url = new URL(GEOCODING_URL);

            url.searchParams.set('access_token', ACCESS_TOKEN);
            url.searchParams.set('limit', String(RESULT_LIMIT));
            url.searchParams.set('q', trimmedQuery);

            const response = await fetch(url);

            if (!response.ok) {
                throw new Error(`Geocoding request failed with status ${response.status}`);
            }

            const data = (await response.json()) as GeocodeResponse;

            return (data.features ?? []).reduce<GeocodeResult[]>((results, feature, index) => {
                const coordinates = feature.geometry?.coordinates;

                if (!coordinates) {
                    return results;
                }

                const { full_address, mapbox_id, name, place_formatted } = feature.properties ?? {};

                results.push({
                    center: coordinates,
                    id: mapbox_id ?? `${index}`,
                    label: full_address ?? place_formatted ?? name ?? trimmedQuery
                });

                return results;
            }, []);
        } finally {
            setIsLoading(false);
        }
    };

    return {
        fetchSuggestions,
        isLoading
    };
};

export default useGeocode;
